import { Archive, ArchiveRestore, Calendar, Globe, Mail, Phone, ShieldAlert, Star, X } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'
import { formatPhoneDisplay } from '@/lib/formatters'
import { formatDateOnly, formatInStoreTz } from '@/lib/time'
import { AvatarCircle } from './components/avatar-circle'
import { CustomerTypeBadge } from './components/customer-type-badge'

import type { Customer } from '@/types'

interface ArchivedCustomerPanelProps {
  customer: Customer
  onClose: () => void
  onRestore: (customer: Customer) => void
  isRestoring?: boolean
  /** Privacy erase — passed only for the owner. */
  onErase?: (customer: Customer) => void
}

/* The side panel for an ARCHIVED customer (directory → Status: Archived).

   Read-only on purpose. The full CustomerSidePanel edits, starts sessions and
   reads loyalty, purchase limits, groups and bank links — all of which belong
   to an active record and refuse an archived one. Here staff see who this is
   and bring them back; everything else returns with Restore. Same card, header
   and info-row styling as the full panel, built only from the list row. */
export function ArchivedCustomerPanel({ customer, onClose, onRestore, isRestoring, onErase }: ArchivedCustomerPanelProps) {
  const fullName = `${customer.firstName} ${customer.lastName}`
  const points = customer.loyaltyProfile?.pointsBalance

  return (
    <Card className="squircle mx-0.5 mb-0.5 w-full md:w-[320px] lg:w-[360px] sticky top-0 flex flex-col h-fit max-h-[calc(100vh-6.5rem)] overflow-hidden animate-in slide-in-from-right duration-300 shadow-lg pb-4">
      <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-2 bg-card z-10">
        <div className="flex items-center gap-1.5 min-w-0">
          <AvatarCircle firstName={customer.firstName} lastName={customer.lastName} avatarUrl={customer.avatarUrl} />
          <div className="min-w-0">
            <CardTitle className="text-base leading-tight truncate">{fullName}</CardTitle>
            <CardDescription className="flex items-center gap-1.5 mt-0.5 flex-wrap">
              <CustomerTypeBadge type={customer.customerType} />
            </CardDescription>
          </div>
        </div>
        <Button variant="ghost" size="icon" onClick={onClose} className="h-8 w-8 -mt-1 shrink-0">
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </Button>
      </CardHeader>

      <CardContent className="grid grid-cols-[minmax(0,1fr)] gap-4 overflow-y-auto overflow-x-hidden pb-0 px-4">
        <div className="flex items-start gap-2 rounded-md border border-border bg-muted/40 p-3 text-xs text-muted-foreground">
          <Archive className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          <span>
            <span className="font-semibold text-foreground">Archived.</span> Hidden from the directory and can&apos;t sign in
            online. Orders, points and purchase history are kept — restore to bring everything back.
          </span>
        </div>

        <Button size="sm" className="w-full" disabled={isRestoring} onClick={() => onRestore(customer)}>
          <ArchiveRestore className="h-3.5 w-3.5 mr-1.5" />Restore customer
        </Button>
        {onErase && (
          <Button size="sm" variant="ghost" className="w-full -mt-2 text-destructive hover:text-destructive" onClick={() => onErase(customer)}>
            <ShieldAlert className="h-3.5 w-3.5 mr-1.5" />Erase personal data…
          </Button>
        )}

        <Separator />

        <div className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Quick Info</p>
          <div className="grid gap-2 text-sm">
            <div className="flex items-center gap-2">
              <Phone className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
              <span className={customer.phone ? '' : 'text-muted-foreground'}>
                {customer.phone ? formatPhoneDisplay(customer.countryCode, customer.phone) : '—'}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Mail className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
              <span className={customer.email ? 'truncate' : 'text-muted-foreground'}>{customer.email ?? '—'}</span>
            </div>
            {customer.dateOfBirth && (
              <div className="flex items-center gap-2">
                <Calendar className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                <span>{formatDateOnly(customer.dateOfBirth, 'MM/dd/yyyy')}</span>
              </div>
            )}
            <div className="flex items-center gap-2">
              <Globe className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
              <span className={customer.hasWebAccount ? '' : 'text-muted-foreground'}>
                {customer.hasWebAccount ? 'Online store account (closed while archived)' : 'No online store account'}
              </span>
            </div>
            {points !== undefined && (
              <div className="flex items-center gap-2">
                <Star className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                <span>{points.toLocaleString()} pts kept</span>
              </div>
            )}
          </div>
        </div>

        <Separator />

        <div className="text-xs text-muted-foreground pb-2">
          <span className="font-medium text-foreground block mb-1">Member since</span>
          {formatInStoreTz(customer.createdAt, 'MMM d, yyyy')}
        </div>
      </CardContent>
    </Card>
  )
}
